import { createSelector } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { RootState } from '../../../store';
import { cutPastDates, fillWeeks, groupAvailabilityPeriods, isOutsideOfSelectableInterval } from '../application';
import { getAvailabilityInfoItems, getCalendarDayJobs, getJobsForSelectedDates } from '../helpers';
import { DayState } from '../ui/Calendar/Day';
import { DayInfoItem } from '../ui/Calendar/Day';

export const candidateAvailabilitySelector = (state: RootState) => state.candidateAvailability;

const actualDatesSelector = createSelector(
    candidateAvailabilitySelector,
    (_: RootState, timeZone: string) => timeZone,
    ({ workCalendar }, timeZone) => ({
        dates: cutPastDates(workCalendar, timeZone),
        timeZone
    })
);

const filledDatesSelector = createSelector(
    actualDatesSelector,
    ({ dates, timeZone }) => fillWeeks(dates, timeZone)
);

const groupedAvailabilityPeriodsSelector = createSelector(
    actualDatesSelector,
    ({ dates }) => groupAvailabilityPeriods(dates)
);

const selectedDatesSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.selectedCalendarRecords
);

const selectableIntervalSelector = createSelector(
    actualDatesSelector,
    ({ dates: availabilityDates }): { start: Date; finish: Date } | null => {
        if (!availabilityDates.length) {
            return null;
        }

        return {
            start: new Date(availabilityDates[0].date),
            finish: new Date(availabilityDates[availabilityDates.length - 1].date)
        };
    },
);

const expiredSelector = createSelector(
    candidateAvailabilitySelector,
    (state) => state.expired
);

const sendingSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.sending
);

const loadingErrorSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.loadingError
);

const sendingErrorSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.sendingError
);

const visibleMonthSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.visibleMonth
);

const onboardingStepSelector = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => availabilityState.onboardingStep
);

const availabilityPageStatus = createSelector(
    candidateAvailabilitySelector,
    (availabilityState) => {
        if (availabilityState.loadingError) {
            return 'error';
        }

        if (availabilityState.loading) {
            return 'loading';
        }

        if (availabilityState.success) {
            return 'success';
        }

        if (availabilityState.workCalendar.length) {
            return 'ready';
        }
    }
);

const workCalendarSelector = createSelector(
    candidateAvailabilitySelector,
    (state) => state.workCalendar || []
);

const workCalendarLoadingSelector = createSelector(
    candidateAvailabilitySelector,
    (state) => state.workCalendarLoading
);

const workCalendarErrorSelector = createSelector(
    candidateAvailabilitySelector,
    (state) => state.workCalendarError
);

const selectedDatesJobsSelector = createSelector(
    selectedDatesSelector,
    (_: RootState, timeZone: string) => timeZone,
    (selectedDates, timeZone) => getJobsForSelectedDates({ selectedDates, timeZone })
);

export interface CalendarDayData {
    date: string;
    dayState: DayState;
    infoItems: DayInfoItem[];
    rawData: any; // Keep for click handler
}

const calendarDayDataSelector = createSelector(
    filledDatesSelector,
    selectedDatesSelector,
    workCalendarSelector,
    actualDatesSelector,
    (_: RootState, timeZone: string) => timeZone,
    (_: RootState, __: string, availableText: string) => availableText,
    (_: RootState, __: string, ___: string, unavailableText: string) => unavailableText,
    (
        filledDates,
        selectedDates,
        workCalendar,
        { dates: availabilityDates },
        timeZone,
        availableText,
        unavailableText
    ): CalendarDayData[] => {
        // Build lookup maps for O(1) access
        const selectedDatesSet = new Set(selectedDates.map(d => d.date));
        const workCalendarMap = new Map<string, typeof workCalendar[0]>();

        workCalendar.forEach(record => {
            const recordDateTz = toZonedTime(record.date, timeZone);
            const dateKey = format(recordDateTz, 'yyyy-MM-dd');
            workCalendarMap.set(dateKey, record);
        });

        // Pre-compute all day data ONCE
        return filledDates.map(day => {
            const dateKey = format(new Date(day.date), 'yyyy-MM-dd');
            const isSelected = selectedDatesSet.has(day.date);
            const isDisabled = isOutsideOfSelectableInterval(availabilityDates, day, timeZone);

            // Compute day state
            const dayState: DayState = isDisabled ? 'Disabled' : isSelected ? 'Selected' : 'Regular';

            // Compute info items
            const availabilityItems = getAvailabilityInfoItems({
                workCalendarRecord: day,
                availableText,
                unavailableText
            });

            const workRecord = workCalendarMap.get(dateKey);
            const jobItems = workRecord ? getCalendarDayJobs({ workCalendarRecord: workRecord, timeZone }) : [];

            const infoItems = [ ...availabilityItems, ...jobItems ]
                .toSorted((a, b) => (a.startTimeTz || '').localeCompare(b.startTimeTz || ''));

            return {
                date: day.date,
                dayState,
                infoItems,
                rawData: day
            };
        });
    }
);

export {
    expiredSelector,
    actualDatesSelector,
    loadingErrorSelector,
    filledDatesSelector,
    selectableIntervalSelector,
    selectedDatesSelector,
    sendingErrorSelector,
    sendingSelector,
    visibleMonthSelector,
    onboardingStepSelector,
    availabilityPageStatus,
    groupedAvailabilityPeriodsSelector,
    workCalendarSelector,
    workCalendarLoadingSelector,
    workCalendarErrorSelector,
    selectedDatesJobsSelector,
    calendarDayDataSelector
};
